import { useState, useContext } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Typography from "@mui/material/Typography";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";

// My Files
import UserAvatar from "../management/UserAvatar";
import firebase from "../../firebase";
import { UserContext, DatabaseContext } from "../../context";
import UserFeed from "../home/UserFeed";
import {
    countUnreadVisibleNotifications,
    useNotificationClock,
} from "../home/notificationTiming";
import useHasCurrentDesktopLicence from "../desktopapp/useCurrentDesktopLicence";

const useStyles = makeStyles((theme) => ({
    menuHeader: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
    },
    userSummary: {
        flexGrow: 1,
        minWidth: 0,
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        flexShrink: 0,
    },
    feedContainer: {
        padding: theme.spacing(1.5, 2, 2),
        backgroundColor: theme.palette.background.default,
    },
    feedHeading: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    avatarDiv: {
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
        borderRadius: "50%",
        transition: "background-color 120ms ease",
        "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.14)",
        },
        "&:focus-visible": {
            outline: "2px solid rgba(255, 255, 255, 0.8)",
            outlineOffset: 2,
        },
    },
}));

export default function UserMenu() {
    const classes = useStyles();

    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const { currentUserData } = useContext(UserContext);
    const { notifications } = useContext(DatabaseContext);
    const now = useNotificationClock(notifications);
    const hasDesktopLicence = useHasCurrentDesktopLicence(
        currentUserData?.key,
        notifications,
    );
    const notificationCount = countUnreadVisibleNotifications(
        notifications,
        currentUserData?.key,
        now,
        hasDesktopLicence,
    );
    const userName =
        [currentUserData?.firstName, currentUserData?.lastName]
            .filter(Boolean)
            .join(" ") || "Your account";

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    function handleSignOutClick() {
        firebase.auth().signOut();
        setAnchorEl(null);
        history.replace("/");
    }

    function handleMyAccountClick() {
        setAnchorEl(null);
        history.push({
            pathname: "/myaccount",
        });
    }

    return (
        <div>
            <Badge badgeContent={notificationCount} color="secondary">
                <div
                    onClick={handleMenu}
                    onKeyDown={handleMenuKeyDown}
                    className={classes.avatarDiv}
                    role="button"
                    tabIndex={0}
                    aria-label="Open account and notifications"
                >
                    <UserAvatar userOrHub={currentUserData} path="users" />
                </div>
            </Badge>
            <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true,
                }}
                PaperProps={{
                    sx: {
                        width: 440,
                        maxWidth: "calc(100vw - 24px)",
                        maxHeight: "calc(100vh - 84px)",
                        mt: 0.75,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        boxShadow: (theme) =>
                            theme.palette.mode === "dark"
                                ? "0 16px 44px rgba(0, 0, 0, 0.46)"
                                : "0 16px 44px rgba(20, 35, 25, 0.18)",
                        overflow: "auto",
                    },
                }}
            >
                <Box className={classes.menuHeader}>
                    <Box className={classes.userSummary}>
                        <Typography
                            variant="subtitle1"
                            noWrap
                            sx={{ fontWeight: 700, lineHeight: 1.25 }}
                        >
                            {userName}
                        </Typography>
                        <Typography
                            variant="caption"
                            noWrap
                            sx={{ display: "block", color: "text.secondary" }}
                        >
                            {currentUserData?.customRoleName ||
                                currentUserData?.role ||
                                "Nodiant user"}
                        </Typography>
                    </Box>
                    <Box className={classes.headerActions}>
                        <Tooltip title="My Account">
                            <IconButton
                                aria-label="My Account"
                                onClick={handleMyAccountClick}
                                size="small"
                                sx={(theme) => ({
                                    color: "primary.main",
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.11,
                                    ),
                                    "&:hover": {
                                        backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.19,
                                        ),
                                    },
                                })}
                            >
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Sign Out">
                            <IconButton
                                aria-label="Sign Out"
                                onClick={handleSignOutClick}
                                size="small"
                                sx={(theme) => ({
                                    color: "secondary.main",
                                    backgroundColor: alpha(
                                        theme.palette.secondary.main,
                                        0.1,
                                    ),
                                    "&:hover": {
                                        backgroundColor: alpha(
                                            theme.palette.secondary.main,
                                            0.17,
                                        ),
                                    },
                                })}
                            >
                                <ExitToAppIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                <Divider />
                <Box className={classes.feedContainer}>
                    <Box className={classes.feedHeading}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                        >
                            Notifications
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                        >
                            {notificationCount === 0
                                ? ""
                                : notificationCount + " unread"}
                        </Typography>
                    </Box>
                    <UserFeed popupFeed />
                </Box>
            </Menu>
        </div>
    );
}
